import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Database, Globe, Shield, Server, ExternalLink } from 'lucide-react';

const SUPABASE_PROJECT_REF = import.meta.env.VITE_SUPABASE_URL?.match(
  /https:\/\/(.+)\.supabase\.co/
)?.[1] || '';

export function SettingsPage() {
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Paramètres</h1>
        <p className="text-gray-600 mt-2">Configuration et informations du projet</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Environnement */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-aspexit-green" />
              <CardTitle className="text-lg">Environnement</CardTitle>
            </div>
            <CardDescription>Configuration de déploiement</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow label="Mode" value={import.meta.env.MODE} />
            <InfoRow
              label="Site URL"
              value={import.meta.env.VITE_SITE_URL || '—'}
              link={import.meta.env.VITE_SITE_URL}
            />
            <InfoRow label="Build" value={import.meta.env.PROD ? 'Production' : 'Development'} />
          </CardContent>
        </Card>

        {/* Supabase */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-aspexit-green" />
              <CardTitle className="text-lg">Supabase</CardTitle>
            </div>
            <CardDescription>Base de données et authentification</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow label="Project Ref" value={SUPABASE_PROJECT_REF} />
            <InfoRow
              label="Dashboard"
              value="Ouvrir le dashboard"
              link={`https://supabase.com/dashboard/project/${SUPABASE_PROJECT_REF}`}
            />
            <InfoRow
              label="Auth Users"
              value="Gérer les utilisateurs"
              link={`https://supabase.com/dashboard/project/${SUPABASE_PROJECT_REF}/auth/users`}
            />
          </CardContent>
        </Card>

        {/* Hébergement */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-aspexit-green" />
              <CardTitle className="text-lg">Hébergement</CardTitle>
            </div>
            <CardDescription>Firebase Hosting</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow
              label="Dev"
              value="aspexit-website-dev.web.app"
              link="https://aspexit-website-dev.web.app"
            />
            <InfoRow
              label="Staging"
              value="aspexit-website-staging.web.app"
              link="https://aspexit-website-staging.web.app"
            />
            <InfoRow
              label="Prod"
              value="aspexit-website.web.app"
              link="https://aspexit-website.web.app"
            />
            <InfoRow
              label="Console Firebase"
              value="Ouvrir"
              link="https://console.firebase.google.com/project/aspexit-website/overview"
            />
          </CardContent>
        </Card>

        {/* Sécurité */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-aspexit-green" />
              <CardTitle className="text-lg">Sécurité</CardTitle>
            </div>
            <CardDescription>Authentification et permissions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow label="Auth Provider" value="Supabase Auth" />
            <InfoRow label="Login public" value="Magic Link (email)" />
            <InfoRow label="Login admin" value="Email + mot de passe" />
            <InfoRow label="RLS" value="Activé sur toutes les tables" />
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">Admin email</span>
              <Badge className="bg-aspexit-green text-white">corentin.leroux39@gmail.com</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  link,
}: {
  label: string;
  value: string;
  link?: string;
}) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-aspexit-green hover:underline flex items-center gap-1"
        >
          {value}
          <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <span className="text-sm font-medium text-gray-900">{value}</span>
      )}
    </div>
  );
}
